import axios from 'axios';
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { usePermissions } from "@/lib/PermissionsContext";
import { AlertTriangle, Plus, Camera, X, Trash2, Clock, MapPin, User, CheckCircle } from "lucide-react";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const TYPE_COLORS = {
  "Accident": "bg-red-100 text-red-800 border-red-200",
  "Dommage matériel": "bg-orange-100 text-orange-800 border-orange-200",
  "Vol": "bg-purple-100 text-purple-800 border-purple-200",
  "Conflit": "bg-yellow-100 text-yellow-800 border-yellow-200",
  "Problème sécurité": "bg-red-100 text-red-800 border-red-200",
  "Qualité des travaux": "bg-blue-100 text-blue-800 border-blue-200",
  "Autre": "bg-slate-100 text-slate-800 border-slate-200",
};

const TYPES_INCIDENT = Object.keys(TYPE_COLORS);

export function OngletRapportIncident({ bon }) {
  const { user, token } = useAuth();
  const { canDo } = usePermissions() || {};
  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC via `bt_incident_supprimer`.
  const canSupprimerIncident = !!(canDo && canDo('bt_incident_supprimer'));
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const fileRef = useRef();

  const [form, setForm] = useState({
    type_incident: "Accident",
    description: "",
    lieu: "",
  });

  const canCreate = ["proprietaire", "charge_projet", "chef_equipe"].includes(user?.role);

  const loadIncidents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/incidents?bon_id=${bon.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 200) {
        const data = res.data;
        setIncidents(data.incidents || []);
      }
    } catch (e) {
      toast.error("Erreur chargement incidents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (bon?.id) loadIncidents();
  }, [bon?.id]);

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedPhotos(prev => [...prev, ...files]);
    const newPreviews = files.map(f => URL.createObjectURL(f));
    setPreviews(prev => [...prev, ...newPreviews]);
  };

  const removePhoto = (idx) => {
    setSelectedPhotos(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!form.description.trim()) {
      toast.error("La description est obligatoire");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("bon_id", bon.id);
      if (bon.dossier_id) fd.append("dossier_id", bon.dossier_id);
      fd.append("type_incident", form.type_incident);
      fd.append("description", form.description);
      fd.append("lieu", form.lieu);
      const { compressImageIfNeeded } = await import('@/lib/imageCompression');
      for (const p of selectedPhotos) {
        const c = await compressImageIfNeeded(p);
        fd.append("photos", c);
      }

      const res = await axios.post(`${API_URL}/api/incidents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.status !== 200) throw new Error("Erreur");
      toast.success("Rapport d'incidence soumis — La chargée de projet et les propriétaires ont été notifiés par SMS");
      setShowForm(false);
      setForm({ type_incident: "Accident", description: "", lieu: "" });
      setSelectedPhotos([]);
      setPreviews([]);
      loadIncidents();
    } catch (e) {
      toast.error("Erreur lors de la soumission");
    } finally {
      setSaving(false);
    }
  };

  const handleResolve = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/incidents/${id}/resolve`, null);
      setIncidents(prev => prev.map(i => i.id === id ? { ...i, resolved: true } : i));
      toast.success("Incident marqué comme résolu — la notification disparaîtra.");
    } catch {
      toast.error("Erreur lors de la résolution");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce rapport ?")) return;
    try {
      await axios.delete(`${API_URL}/api/incidents/${id}`);
      toast.success("Rapport supprimé");
      loadIncidents();
    } catch (e) {
      toast.error("Erreur suppression");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" });
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-500" />
          <h3 className="font-semibold text-slate-800">Rapports d'Incidence</h3>
          {incidents.length > 0 && (
            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
              {incidents.length}
            </Badge>
          )}
        </div>
        {canCreate && !showForm && (
          <Button
            size="sm"
            onClick={() => setShowForm(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="btn-nouveau-incident"
          >
            <Plus className="w-4 h-4 mr-1" /> Signaler un incident
          </Button>
        )}
      </div>

      {/* Formulaire de création */}
      {showForm && (
        <div className="border border-orange-200 bg-orange-50 rounded-lg p-4 space-y-3">
          <h4 className="font-semibold text-orange-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Nouveau rapport d'incidence
          </h4>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600 font-medium">Type d'incident *</label>
              <Select value={form.type_incident} onValueChange={v => setForm(f => ({ ...f, type_incident: v }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES_INCIDENT.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">Lieu / Localisation</label>
              <Input
                className="mt-1"
                placeholder="Ex: Pièce principale, escalier..."
                value={form.lieu}
                onChange={e => setForm(f => ({ ...f, lieu: e.target.value }))}
                data-testid="input-incident-lieu"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-600 font-medium">Description détaillée *</label>
            <Textarea
              className="mt-1 min-h-[100px]"
              placeholder="Décrivez ce qui s'est passé, les circonstances, les personnes impliquées..."
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              data-testid="textarea-incident-description"
            />
          </div>

          {/* Photos */}
          <div>
            <label className="text-xs text-slate-600 font-medium">Photos (optionnel)</label>
            <div className="mt-1 flex flex-wrap gap-2">
              {previews.map((src, i) => (
                <div key={src.id || src._id || i} className="relative w-20 h-20 rounded overflow-hidden border">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => removePhoto(i)}
                    className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => fileRef.current?.click()}
                className="w-20 h-20 border-2 border-dashed border-slate-300 rounded flex flex-col items-center justify-center text-slate-400 hover:border-orange-400 hover:text-orange-500 transition-colors"
              >
                <Camera className="w-5 h-5" />
                <span className="text-[10px] mt-1">Ajouter</span>
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handlePhotoSelect}
              />
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="btn-soumettre-incident"
            >
              {saving ? "Envoi..." : "Soumettre le rapport"}
            </Button>
            <Button
              variant="outline"
              onClick={() => { setShowForm(false); setSelectedPhotos([]); setPreviews([]); }}
              disabled={saving}
            >
              Annuler
            </Button>
          </div>
          <p className="text-xs text-orange-700">
            La chargée de projet et les propriétaires recevront un SMS dès la soumission.
          </p>
        </div>
      )}

      {/* Liste des incidents */}
      {loading ? (
        <p className="text-sm text-slate-400 text-center py-4">Chargement...</p>
      ) : incidents.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          <AlertTriangle className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Aucun rapport d'incidence pour ce bon de travail</p>
        </div>
      ) : (
        <div className="space-y-3">
          {incidents.map(incident => (
            <div key={incident.id} className="border border-slate-200 rounded-lg p-4 bg-white">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${TYPE_COLORS[incident.type_incident] || TYPE_COLORS["Autre"]}`}>
                    {incident.type_incident}
                  </span>
                  {incident.lieu && (
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {incident.lieu}
                    </span>
                  )}
                  {incident.resolved && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded border bg-green-100 text-green-700 border-green-200 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Résolu
                    </span>
                  )}
                </div>
                <div className="flex gap-1">
                  {!incident.resolved && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleResolve(incident.id)}
                      className="text-green-600 hover:text-green-800 hover:bg-green-50 h-7 px-2 text-xs gap-1"
                      data-testid="btn-resoudre-incident"
                    >
                      <CheckCircle className="w-3.5 h-3.5" /> Résolu
                    </Button>
                  )}
                  {/* 🆕 [Phase v13.61 Vague 5b — 2026-08-25] RBAC `bt_incident_supprimer` (Owner-only par défaut). */}
                  {canSupprimerIncident && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(incident.id)}
                      className="text-red-400 hover:text-red-600 h-7 w-7 p-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              <p className="text-sm text-slate-700 whitespace-pre-wrap mb-3">{incident.description}</p>

              {incident.photos?.length > 0 && (
                <div className="flex gap-2 mb-3 flex-wrap">
                  {incident.photos.map((url, i) => (
                    <a key={url.id || url._id || i} href={`${API_URL}${url}`} target="_blank" rel="noopener noreferrer">
                      <img
                        src={`${API_URL}${url}`}
                        alt=""
                        className="w-16 h-16 object-cover rounded border hover:opacity-80 transition-opacity"
                      />
                    </a>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-3 text-xs text-slate-400 pt-2 border-t border-slate-100">
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" /> {incident.created_by_nom}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {formatDate(incident.created_at)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
